/**
 * 自研 PDF 压缩器（替代已废弃的 @quicktoolsone/pdf-compress）
 * 设计要点：
 *  1. 不用 pdf.js worker（规避 MIME / 缓存问题），改用浏览器原生 createImageBitmap 解码图片。
 *  2. 结构优化（Pass A）：对象流压缩 + 删除页面缩略图（安全、不破坏内容）。
 *  3. 图片重压（Pass B）：逐页在 XObject 级别重压图片——只替换图片流，文字 / 矢量完全不动。
 *     - 仅处理 DCTDecode(JPEG) 图片；
 *     - 有 SMask / Mask（透明蒙版）或使用非简单色彩空间(Indexed / ICCBased / Separation)的跳过，避免破坏显示；
 *     - 解码 → 按 maxPixels 缩放 → 重编码 JPEG(quality) → 替换引用并删除旧流（真正释放体积）。
 *  4. 失败优雅降级：单张图片处理失败不影响整体；图片重压无收益则保留结构优化结果。
 */
import {
  PDFDocument,
  PDFName,
  PDFDict,
  PDFRawStream,
  PDFArray,
} from 'pdf-lib'

// preset → JPEG 质量 / 最大边长（像素）。lossless 不重压图片。
const PRESET_QUALITY = { lossless: null, balanced: 0.72, max: 0.55 }
const PRESET_MAX_PIXELS = { lossless: null, balanced: 2200, max: 1600 }
// 小于此体积的图片不值得重压（避免对 tiny 图标做无谓解码）
const MIN_IMAGE_BYTES = 10 * 1024

function str(x) {
  if (!x) return ''
  return String(x)
}

function hasFilter(dict, filterName) {
  const f = dict.lookup(PDFName.of('Filter'))
  if (!f) return false
  if (str(f) === '/' + filterName) return true
  if (f instanceof PDFArray) {
    return f.asArray().some((it) => str(it) === '/' + filterName)
  }
  return false
}

function hasMask(dict) {
  return !!dict.lookup(PDFName.of('SMask')) || !!dict.lookup(PDFName.of('Mask'))
}

function simpleColorSpace(dict) {
  const cs = dict.lookup(PDFName.of('ColorSpace'))
  if (!cs) return true // 缺省交给 embedJpg 按 JPEG 实际色彩决定
  const s = str(cs)
  // 显式 CMYK 的 JPEG 用浏览器原生解码可能变色，直接跳过（保留原图，绝不损坏内容）
  if (s === '/DeviceCMYK') return false
  return ['/DeviceGray', '/DeviceRGB'].includes(s)
}

function isRecompressableImage(dict) {
  if (!dict) return false
  if (str(dict.lookup(PDFName.of('Subtype'))) !== '/Image') return false
  if (!hasFilter(dict, 'DCTDecode')) return false
  if (hasMask(dict)) return false
  if (!simpleColorSpace(dict)) return false
  return true
}

// 浏览器原生解码 + 缩放 + 重编码 JPEG。失败返回 null（调用方保留原图）。
async function tryRecompress(rawBytes, quality, maxPixels) {
  try {
    if (typeof createImageBitmap !== 'function' || typeof document === 'undefined') {
      return null
    }
    const blob = new Blob([rawBytes], { type: 'image/jpeg' })
    const bmp = await createImageBitmap(blob)
    const width = bmp.width
    const height = bmp.height
    const maxSide = Math.max(width, height)
    let scale = 1
    if (maxSide > maxPixels) scale = maxPixels / maxSide
    const nw = Math.max(1, Math.round(width * scale))
    const nh = Math.max(1, Math.round(height * scale))
    const canvas = document.createElement('canvas')
    canvas.width = nw
    canvas.height = nh
    const ctx = canvas.getContext('2d', { alpha: false })
    if (!ctx) return null
    ctx.drawImage(bmp, 0, 0, nw, nh)
    if (typeof bmp.close === 'function') bmp.close()
    const outBlob = await new Promise((res) => canvas.toBlob(res, 'image/jpeg', quality))
    if (!outBlob) return null
    const buf = await outBlob.arrayBuffer()
    return new Uint8Array(buf)
  } catch (e) {
    return null
  }
}

function removeThumbnails(pdfDoc) {
  try {
    for (const page of pdfDoc.getPages()) {
      const node = page.node
      const dict = node.dict
      if (dict && dict.lookup(PDFName.of('Thumb'))) {
        dict.delete(PDFName.of('Thumb'))
      }
    }
  } catch (e) {
    // 缩略图清理属优化项，失败不影响压缩
  }
}

// 收集文档内所有可重压图片引用（含 Form XObject 内嵌资源），按 ref 去重
function collectImageRefs(pdfDoc) {
  const items = []
  const seen = new Map() // "objNum:gen" -> index
  const visit = (resourcesDict) => {
    if (!resourcesDict) return
    const xobjects = resourcesDict.lookupMaybe(PDFName.of('XObject'), PDFDict)
    if (!xobjects) return
    for (const [name, ref] of xobjects.entries()) {
      const obj = pdfDoc.context.lookup(ref)
      if (obj instanceof PDFRawStream) {
        const dict = obj.dict
        if (isRecompressableImage(dict)) {
          const key = ref.objectNumber + ':' + ref.generationNumber
          if (!seen.has(key)) {
            seen.set(key, items.length)
            items.push({ ref, obj, holders: [] })
          }
          items[seen.get(key)].holders.push({ dict: xobjects, name })
        }
      } else if (obj instanceof PDFDict) {
        // 可能是 Form XObject，递归其 Resources
        const formResources = obj.lookupMaybe(PDFName.of('Resources'), PDFDict)
        if (formResources) visit(formResources)
      }
    }
  }
  for (const page of pdfDoc.getPages()) {
    const node = page.node
    let res = null
    if (typeof node.Resources === 'function') {
      res = node.Resources()
    } else if (node.dict) {
      res = node.dict.lookupMaybe(PDFName.of('Resources'), PDFDict)
    }
    visit(res)
  }
  return items
}

async function recompressImages(pdfDoc, { quality, maxPixels, onProgress }) {
  const items = collectImageRefs(pdfDoc)
  let before = 0
  let after = 0
  const total = items.length
  let done = 0
  for (const item of items) {
    const raw = item.obj.asUint8Array()
    const rawLen = raw.length
    before += rawLen
    if (rawLen >= MIN_IMAGE_BYTES) {
      const newBytes = await tryRecompress(raw, quality, maxPixels)
      if (newBytes && newBytes.length < rawLen) {
        try {
          const newImg = await pdfDoc.embedJpg(newBytes)
          for (const h of item.holders) h.dict.set(h.name, newImg.ref)
          pdfDoc.context.delete(item.ref) // 删除旧图片流，真正释放体积
          after += newBytes.length
        } catch (e) {
          after += rawLen // 嵌入失败则保留原图
        }
      } else {
        after += rawLen
      }
    } else {
      after += rawLen
    }
    done++
    if (onProgress && total > 0) {
      onProgress({
        phase: 'images',
        progress: 10 + Math.round((done / total) * 75),
        message: `重压图片 ${done}/${total}`,
      })
    }
  }
  return { before, after }
}

/**
 * 压缩 PDF。
 * @param {ArrayBuffer|Uint8Array} arrayBuffer 原始 PDF 数据
 * @param {object} options
 *   preset: 'lossless' | 'balanced' | 'max'（默认 'max'）
 *   jpegQuality / maxPixels: 覆盖 preset 默认值
 *   onProgress: (e)=>{}  e={phase,progress,message}
 * @returns {Promise<{pdf:Uint8Array, stats:object}>}
 */
export async function compress(arrayBuffer, options = {}) {
  const {
    preset = 'max',
    onProgress = () => {},
    jpegQuality = null,
    maxPixels = null,
  } = options

  if (!(arrayBuffer instanceof ArrayBuffer) && !(arrayBuffer instanceof Uint8Array)) {
    throw new Error('pdfBuffer 必须是 ArrayBuffer 或 Uint8Array')
  }
  if (arrayBuffer.byteLength === 0) {
    throw new Error('PDF buffer 为空')
  }

  const quality =
    jpegQuality != null ? jpegQuality : PRESET_QUALITY[preset] ?? 0.7
  const mp = maxPixels != null ? maxPixels : PRESET_MAX_PIXELS[preset] ?? 2000
  const doImages = quality != null && mp != null

  const originalSize = arrayBuffer.byteLength
  // 复制一份，避免 pdf-lib 解析时对传入 buffer 产生副作用
  const ab =
    arrayBuffer instanceof Uint8Array
      ? arrayBuffer.slice().buffer
      : arrayBuffer.slice(0)

  onProgress({ phase: 'loading', progress: 0, message: '读取PDF...' })
  const pdfDoc = await PDFDocument.load(ab, {
    updateMetadata: false,
    ignoreEncryption: true,
    throwOnInvalidObject: false,
  })

  onProgress({ phase: 'optimizing', progress: 5, message: '结构优化...' })
  removeThumbnails(pdfDoc)

  let imageBefore = 0
  let imageAfter = 0
  if (doImages) {
    const r = await recompressImages(pdfDoc, {
      quality,
      maxPixels: mp,
      onProgress,
    })
    imageBefore = r.before
    imageAfter = r.after
  }

  onProgress({ phase: 'saving', progress: 90, message: '保存...' })
  const outBytes = await pdfDoc.save({
    useObjectStreams: true,
    addDefaultPage: false,
  })
  const compressedSize = outBytes.byteLength
  const bytesSaved = originalSize - compressedSize
  const percentageSaved = originalSize > 0 ? (bytesSaved / originalSize) * 100 : 0

  onProgress({ phase: 'complete', progress: 100, message: '压缩完成' })

  return {
    pdf: outBytes,
    stats: {
      originalSize,
      compressedSize,
      bytesSaved,
      percentageSaved,
      ratio: originalSize > 0 ? compressedSize / originalSize : 1,
      presetUsed: preset,
      imageBytesBefore: imageBefore,
      imageBytesAfter: imageAfter,
      imageBytesSaved: imageBefore - imageAfter,
    },
  }
}

export default { compress }
